import axios from 'axios';
import type { AuthMode } from './types';

// Un-interceptored instance so auth calls never attach a stale bearer or
// recursively refresh. nginx (prod) / Vite (dev) proxy /api to the backend.
const raw = axios.create({ baseURL: '/api', timeout: 30000 });

export interface TokenResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

interface RawTokenResponse {
  access_token?: string;
  id_token?: string;
  refresh_token?: string | null;
  expires_in?: number;
  token_type?: string;
}

function normalize(d: RawTokenResponse): TokenResponse {
  return {
    accessToken: d.access_token ?? '',
    idToken: d.id_token ?? d.access_token ?? '',
    refreshToken: d.refresh_token ?? '',
    expiresIn: d.expires_in ?? 0,
    tokenType: d.token_type ?? 'Bearer',
  };
}

export async function getAuthMode(): Promise<AuthMode> {
  try {
    const res = await raw.get<{ mode: AuthMode }>('/auth/mode');
    return res.data.mode === 'zitadel' ? 'zitadel' : 'local';
  } catch {
    return 'local';
  }
}

/** `login` is the username (local mode) or email (Zitadel mode). */
export async function login(loginName: string, password: string): Promise<TokenResponse> {
  const res = await raw.post<RawTokenResponse>('/auth/login', { email: loginName, password });
  return normalize(res.data);
}

export async function refresh(refreshToken: string): Promise<TokenResponse> {
  const res = await raw.post<RawTokenResponse>('/auth/refresh', { refresh_token: refreshToken });
  return normalize(res.data);
}

export async function logout(accessToken: string | null, refreshToken: string | null): Promise<void> {
  try {
    await raw.post('/auth/logout', { access_token: accessToken, refresh_token: refreshToken || null });
  } catch {
    // Revoke is best-effort; local tokens are cleared regardless.
  }
}

export interface VerifyResponse {
  valid: boolean;
  claims: Record<string, unknown> | null;
}

export async function verifyToken(accessToken: string): Promise<VerifyResponse> {
  const res = await raw.post<VerifyResponse>('/auth/verify-token', { access_token: accessToken });
  return res.data;
}
